/** Download direction and references from the same version the user approved. */
import { useState, useRef, useEffect } from "react";
import { api, ApiError } from "../api/client";
import { manifestUrlOf } from "../api/versions";
import { WAYS_OUT, say } from "../copy/outputs";
import { handoffZip, type Handoff } from "../film/handoff";
import { parseManifest } from "../film/manifest";
import { useFrameWrap } from "../film/useFrame";
import { Button } from "../ui";
import { saveBlob } from "./FramesZip";

export function GenerationPackage({
  adId,
  version,
  language,
  save = saveBlob,
}: {
  adId: string;
  version: number;
  language?: string;
  save?: (blob: Blob, name: string) => void;
}) {
  const wrap = useFrameWrap();
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), []);
  const [busy, setBusy] = useState(false);
  const [includeRequested, setIncludeRequested] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const run = async () => {
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setResult(null);
    setFailed(null);
    try {
      const params = new URLSearchParams({ version: String(version) });
      if (language) params.set("language", language);
      const handoff = await api<Handoff>(
        `/v1/ads/${encodeURIComponent(adId)}/handoff?${params}`,
        { signal: controller.signal },
      );
      if (
        handoff.version !== version ||
        (language && handoff.language !== language)
      )
        throw new Error("The described version changed");
      const baseManifest = parseManifest(
        await api<unknown>(manifestUrlOf(adId, handoff.version), {
          signal: controller.signal,
        }),
      );
      const translated = handoff.preview_patches ?? [];
      const manifest = translated.length
        ? {
            ...baseManifest,
            patches: [
              ...(baseManifest.patches ?? []).filter(
                (p) => p.kind !== "captions",
              ),
              ...translated,
            ],
            scenes: baseManifest.scenes.map((scene) => {
              const patch = translated.find(
                (p) => p.kind === "captions" && p.scene === scene.id,
              );
              return patch?.kind === "captions"
                ? { ...scene, captions: patch.captions }
                : scene;
            }),
          }
        : baseManifest;
      const made = await handoffZip(handoff, manifest, {
        includeRequested,
        signal: controller.signal,
        wrap,
      });
      controller.signal.throwIfAborted();
      save(
        made.zip,
        `truereel-${adId.slice(0, 8)}-generation-v${handoff.version}-${handoff.language}.zip`,
      );
      setResult(
        made.missing.length
          ? say(WAYS_OUT.elsewhere.packageMissing, { n: made.missing.length })
          : includeRequested && !handoff.requested_changes
            ? WAYS_OUT.elsewhere.packageNoRequested
            : WAYS_OUT.elsewhere.packageDone,
      );
    } catch (error) {
      if (controller.signal.aborted) {
        setResult(WAYS_OUT.elsewhere.packageCancelled);
        return;
      }
      setFailed(
        error instanceof ApiError && error.code === "handoff_too_large"
          ? WAYS_OUT.elsewhere.packageTooLarge
          : WAYS_OUT.elsewhere.packageFailed,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="flex flex-col items-start gap-2"
      data-testid="generation-package"
    >
      <p className="m-0 text-sm text-slate-600">
        {WAYS_OUT.elsewhere.packageHint}
      </p>
      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={includeRequested}
          disabled={busy}
          onChange={(e) => setIncludeRequested(e.target.checked)}
        />
        {WAYS_OUT.elsewhere.includeRequested}
      </label>
      <Button
        type="button"
        variant="primary"
        disabled={busy}
        aria-busy={busy}
        onClick={() => void run()}
      >
        {busy ? WAYS_OUT.elsewhere.packaging : WAYS_OUT.elsewhere.package}
      </Button>
      {busy ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => active.current?.abort()}
        >
          {WAYS_OUT.elsewhere.cancelPackage}
        </Button>
      ) : null}
      {result ? (
        <p className="m-0 text-sm text-slate-700" role="status">
          {result}
        </p>
      ) : null}
      {failed ? (
        <p className="m-0 text-sm text-slate-700" role="alert">
          {failed}
        </p>
      ) : null}
    </div>
  );
}
